import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/admin/tickets`);
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, []);

  const openTicket = async (ticket) => {
    setSelected(ticket);
    try {
      const response = await axios.get(`${API}/admin/tickets/${ticket.id}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await axios.post(`${API}/admin/tickets/${selected.id}/reply`, { message: reply });
      toast.success('Reply sent');
      setReply('');
      const response = await axios.get(`${API}/admin/tickets/${selected.id}/messages`);
      setMessages(response.data);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  return (
    <div className="p-8" data-testid="admin-tickets-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">Support Tickets</h1>
        <p className="text-slate-600 mt-1">{tickets.length} tickets</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Client</th>
              <th className="p-4">Subject</th>
              <th className="p-4">Priority</th>
              <th className="p-4">Status</th>
              <th className="p-4">Created</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
              : tickets.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-slate-500">No tickets</td></tr>
              : tickets.map(ticket => (
                <tr key={ticket.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-4">
                    <div className="font-medium">{ticket.client_name}</div>
                    <div className="text-xs text-slate-500">{ticket.client_email}</div>
                  </td>
                  <td className="p-4">{ticket.subject}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      ticket.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                      ticket.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-slate-100 text-slate-600'
                    }`}>{ticket.priority}</span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">{ticket.status}</span>
                  </td>
                  <td className="p-4 text-xs text-slate-500">{new Date(ticket.created_at).toLocaleString()}</td>
                  <td className="p-4">
                    <Button size="sm" onClick={() => openTicket(ticket)} data-testid={`admin-view-ticket-${ticket.id}`}>View</Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{selected?.subject}</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3">
            {messages.map(msg => (
              <div key={msg.id} className={`p-3 rounded-lg ${msg.is_admin ? 'bg-blue-50 ml-8' : 'bg-slate-50 mr-8'}`}>
                <div className="text-xs text-slate-500 mb-1">{msg.is_admin ? 'Admin' : 'Client'} - {new Date(msg.created_at).toLocaleString()}</div>
                <div>{msg.message}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." data-testid="admin-ticket-reply" />
            <Button onClick={sendReply} data-testid="admin-send-reply">Send Reply</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTickets;
